import { useEffect, useState } from "react";
import { openDb, listConnections, putConnection } from "./storage/indexeddb";
import type {
  StoredConnection,
  M3UConfig,
  JellyfinConfig,
  PlexConfig,
  XtreamConfig,
  StalkerConfig,
} from "./storage/schema";
import type { M3UChannel } from "./adapters/static/m3u";
import { M3UConnectStep } from "./components/setup/M3UConnectStep";
import { JellyfinConnectStep } from "./components/setup/JellyfinConnectStep";
import { PlexConnectStep } from "./components/setup/PlexConnectStep";
import { XtreamConnectStep } from "./components/setup/XtreamConnectStep";
import { StalkerConnectStep } from "./components/setup/StalkerConnectStep";
import { LibraryGrid } from "./components/LibraryGrid";
import { Player } from "./components/Player";
import { ServerPicker, type ProviderKey } from "./components/ServerPicker";
import { JellyfinAdapter } from "./adapters/static/jellyfin";
import { PlexAdapter } from "./adapters/static/plex";
import { XtreamAdapter } from "./adapters/relayed/xtream";
import { StalkerAdapter } from "./adapters/relayed/stalker";

type LibraryItem = {
  id: string;
  name: string;
  url: string;
  logo: string | null;
  __stalker?: { cmd: string; portal: string; mac: string };
};

type View =
  | { name: "home" }
  | { name: "picker" }
  | { name: "connect"; provider: ProviderKey }
  | { name: "library"; title: string; items: LibraryItem[] };

export function App() {
  const [view, setView] = useState<View>({ name: "home" });
  const [connections, setConnections] = useState<StoredConnection[]>([]);
  const [playerState, setPlayerState] = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    (async () => {
      const db = await openDb();
      const list = await listConnections(db);
      setConnections(list);
    })();
  }, []);

  const handleLibrarySelect = async (item: LibraryItem) => {
    if (item.__stalker) {
      try {
        const streamUrl = await StalkerAdapter.resolveStreamUrl(
          item.__stalker.portal,
          item.__stalker.mac,
          item.__stalker.cmd
        );
        setPlayerState({ url: streamUrl, name: item.name });
        return;
      } catch (e) {
        console.error("Failed to resolve Stalker stream:", e);
      }
    }
    setPlayerState({ url: item.url, name: item.name });
  };

  const saveConnection = async (
    providerType: StoredConnection["providerType"],
    name: string,
    config: StoredConnection["config"]
  ): Promise<StoredConnection> => {
    const db = await openDb();
    const conn: StoredConnection = {
      id: crypto.randomUUID(),
      providerType,
      name,
      createdAt: Date.now(),
      lastUsedAt: Date.now(),
      config,
    };
    await putConnection(db, conn);
    const updated = await listConnections(db);
    setConnections(updated);
    return conn;
  };

  return (
    <main style={{ minHeight: "100vh", background: "#1a1a2e", color: "#fff" }}>
      <header style={{ padding: 16, borderBottom: "1px solid #333" }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>StreamVault Media</h1>
        <button onClick={() => setView({ name: "picker" })}>Add server</button>
        {connections.length > 0 && (
          <ul>
            {connections.map((c) => (
              <li key={c.id}>
                {c.name} ({c.providerType})
              </li>
            ))}
          </ul>
        )}
      </header>

      {view.name === "home" && (
        <div style={{ padding: 24 }}>
          <p>Welcome. Add a server to get started.</p>
          {connections.length === 0 && <p>No connections yet.</p>}
        </div>
      )}

      {view.name === "picker" && (
        <ServerPicker onSelect={(p) => setView({ name: "connect", provider: p })} />
      )}

      {view.name === "connect" && view.provider === "m3u" && (
        <M3UConnectStep
          onConnected={(channels: M3UChannel[], url: string) => {
            void saveConnection("m3u", new URL(url).hostname, { kind: "m3u", playlistUrl: url } as M3UConfig);
            setView({
              name: "library",
              title: new URL(url).hostname,
              items: channels.map((c, i) => ({ id: `${i}`, name: c.name, url: c.url, logo: c.logo })),
            });
          }}
        />
      )}

      {view.name === "connect" && view.provider === "jellyfin" && (
        <JellyfinConnectStep
          onConnected={(items, baseUrl, apiKey, userId, serverName) => {
            void saveConnection("jellyfin", serverName, {
              kind: "jellyfin",
              baseUrl,
              apiKey,
              userId,
              serverName,
            } as JellyfinConfig);
            setView({
              name: "library",
              title: serverName,
              items: items.map((it) => ({
                id: it.Id,
                name: it.Name,
                url: JellyfinAdapter.getStreamUrl(baseUrl, apiKey, it.Id),
                logo: it.ImageTags?.Primary
                  ? `${baseUrl}/Items/${it.Id}/Images/Primary?api_key=${apiKey}`
                  : null,
              })),
            });
          }}
        />
      )}

      {view.name === "connect" && view.provider === "plex" && (
        <PlexConnectStep
          onConnected={(server, token, machineId) => {
            const best = server.connections[0];
            const baseUrl = best.uri;
            void saveConnection("plex", server.name, {
              kind: "plex",
              baseUrl,
              accessToken: token,
              machineId,
              serverName: server.name,
            } as PlexConfig);
            PlexAdapter.getLibrary(baseUrl, token, machineId).then((lib) => {
              setView({
                name: "library",
                title: server.name,
                items: lib.items.map((it) => ({
                  id: it.ratingKey,
                  name: it.title,
                  url: PlexAdapter.getStreamUrl(baseUrl, it.ratingKey, token, machineId),
                  logo: it.thumb ? `${baseUrl}${it.thumb}?X-Plex-Token=${token}` : null,
                })),
              });
            });
          }}
        />
      )}

      {view.name === "connect" && view.provider === "xtream" && (
        <XtreamConnectStep
          onConnected={(channels, base, user, pass) => {
            void saveConnection("xtream", new URL(base).hostname, {
              kind: "xtream",
              baseUrl: base,
              username: user,
              password: pass,
            } as XtreamConfig);
            setView({
              name: "library",
              title: new URL(base).hostname,
              items: channels.map((c) => ({
                id: String(c.stream_id),
                name: c.name,
                url: XtreamAdapter.getStreamUrl(base, user, pass, c.stream_id),
                logo: c.stream_icon,
              })),
            });
          }}
        />
      )}

      {view.name === "connect" && view.provider === "stalker" && (
        <StalkerConnectStep
          onConnected={(channels, portal, mac) => {
            void saveConnection("stalker", new URL(portal).hostname, {
              kind: "stalker",
              portal,
              mac,
            } as StalkerConfig);
            setView({
              name: "library",
              title: new URL(portal).hostname,
              items: channels.map((c, i) => ({
                id: String(i),
                name: c.name,
                url: c.url ?? "",
                logo: c.logo,
                __stalker: c.url ? { cmd: c.url, portal, mac } : undefined,
              })),
            });
          }}
        />
      )}

      {view.name === "library" && (
        <LibraryGrid
          channels={view.items.map((it) => ({
            name: it.name,
            url: it.url,
            tvgId: null,
            tvgName: null,
            logo: it.logo,
            group: view.title,
            streamType: "unknown" as const,
          }))}
          onSelect={(ch) => {
            const item = view.items.find((it) => it.url === ch.url && it.name === ch.name);
            void handleLibrarySelect(
              item ?? { id: ch.name, name: ch.name, url: ch.url, logo: ch.logo }
            );
          }}
        />
      )}

      {playerState && <Player url={playerState.url} onClose={() => setPlayerState(null)} />}
    </main>
  );
}
